import { useEffect, useState } from 'react';
import { api, ErreurApi } from '../api.js';
import { tokens } from '../tokens.js';
import {
  Badge, BoiteErreur, labelChamp, inputBase,
  boutonPrincipal, boutonSecondaire,
} from '../helpers.jsx';
import { Plus, Boxes, Trash2, Copy, Eye, EyeOff } from 'lucide-react';

// CRUD applications (sources futures de tickets via widget).
export function AdminApplications() {
  const [apps, setApps]      = useState([]);
  const [chargement, setCh]  = useState(true);
  const [erreur, setErreur]  = useState('');
  const [creerOuvert, setCreer] = useState(false);
  const [nomNouveau, setNomNew] = useState('');
  const [secretVisible, setSV]  = useState({});

  function recharger() {
    setCh(true);
    api.get('/admin/applications')
      .then(d => setApps(d.applications || []))
      .catch(e => setErreur(e.message))
      .finally(() => setCh(false));
  }
  useEffect(recharger, []);

  async function creer() {
    if (nomNouveau.trim() === '') return;
    try {
      await api.post('/admin/applications', { nom: nomNouveau.trim() });
      setCreer(false); setNomNew('');
      recharger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Erreur');
    }
  }

  async function basculer(a) {
    try {
      await api.put(`/admin/applications/${a.id}`, { actif: !a.actif });
      recharger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Erreur');
    }
  }

  function copier(texte) {
    navigator.clipboard?.writeText(texte);
  }

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 18 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: tokens.ink }}>
            Applications
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: tokens.inkSoft }}>
            Sources qui pourront utiliser le widget. La clé publique sert à identifier la source ;
            la clé secrète signera de futurs webhooks.
          </p>
        </div>
        <button onClick={() => setCreer(true)} style={boutonPrincipal}>
          <Plus size={16} /> Nouvelle application
        </button>
      </div>

      {erreur && <BoiteErreur message={erreur} />}
      {chargement && <div style={etat}>Chargement…</div>}

      {!chargement && apps.length === 0 && (
        <div style={etat}>
          <Boxes size={28} color={tokens.inkMuted} />
          <div style={{ marginTop: 10 }}>Aucune application déclarée.</div>
        </div>
      )}

      {!chargement && apps.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', gap: 10 }}>
          {apps.map(a => (
            <div key={a.id} style={{
              background: tokens.surface,
              border: `1px solid ${tokens.border}`,
              borderRadius: 10,
              padding: 16,
              opacity: a.actif ? 1 : 0.5,
            }}>
              <div style={{ display:'flex', alignItems:'center', gap: 10, marginBottom: 10 }}>
                <Boxes size={16} color={tokens.accent} />
                <strong style={{ fontSize: 14, color: tokens.ink }}>{a.nom}</strong>
                {!a.actif && <Badge bg={tokens.dangerBg} fg={tokens.danger} taille="petit">Inactif</Badge>}
                <span style={{ fontSize: 11, color: tokens.inkMuted }}>
                  {a.nb_services} service{a.nb_services > 1 ? 's' : ''} liés
                </span>
                <div style={{ marginLeft:'auto', display:'flex', gap: 6 }}>
                  <button onClick={() => basculer(a)}
                    style={{ ...boutonSecondaire, padding: '4px 10px', fontSize: 12 }}
                  >
                    {a.actif ? 'Désactiver' : 'Réactiver'}
                  </button>
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <ChampCopiable label="Clé publique" valeur={a.cle_publique} onCopier={copier} />
                <ChampCopiable
                  label="Clé secrète"
                  valeur={secretVisible[a.id] ? a.cle_secrete : '••••••••••••••••••••••••••••••••'}
                  onCopier={copier}
                  onToggle={() => setSV(s => ({ ...s, [a.id]: !s[a.id] }))}
                  visible={!!secretVisible[a.id]}
                  brute={a.cle_secrete}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {creerOuvert && (
        <div style={{
          position:'fixed', inset: 0, zIndex: 50, background:'rgba(0,0,0,0.5)',
          display:'flex', alignItems:'center', justifyContent:'center', padding: 20,
        }}>
          <div style={{ background: tokens.surface, borderRadius: 12, maxWidth: 400, width:'100%', padding: 24, boxShadow: '0 10px 40px rgba(0,0,0,0.20)' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 600 }}>Nouvelle application</h3>
            <label style={labelChamp}>Nom</label>
            <input
              autoFocus
              value={nomNouveau}
              onChange={(e) => setNomNew(e.target.value)}
              placeholder="Ex : Dolibarr, AMC Paie, SaaS Atelier"
              style={inputBase}
            />
            <div style={{ display:'flex', gap: 8, justifyContent:'flex-end', marginTop: 16 }}>
              <button onClick={() => setCreer(false)} style={boutonSecondaire}>Annuler</button>
              <button onClick={creer} disabled={nomNouveau.trim() === ''} style={boutonPrincipal}>Créer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChampCopiable({ label, valeur, onCopier, onToggle, visible, brute }) {
  const aCopier = brute ?? valeur;
  return (
    <div>
      <label style={{ ...labelChamp, marginBottom: 4 }}>{label}</label>
      <div style={{ display:'flex', alignItems:'center', gap: 4 }}>
        <input
          readOnly
          value={valeur}
          style={{ ...inputBase, fontFamily: 'monospace', fontSize: 12, padding: '6px 10px' }}
        />
        {onToggle && (
          <button onClick={onToggle} title={visible ? 'Cacher' : 'Afficher'}
            style={iconBtn}
          >
            {visible ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        )}
        <button onClick={() => onCopier(aCopier)} title="Copier" style={iconBtn}>
          <Copy size={14} />
        </button>
      </div>
    </div>
  );
}

const iconBtn = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 6,
  padding: 6,
  cursor:'pointer',
  color: tokens.inkSoft,
};
const etat = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 10,
  padding: 32,
  textAlign:'center',
  color: tokens.inkSoft,
  fontSize: 14,
};
